/* Native Dawn form remains the only source of price, variant, stock and cart state. */
(() => {
  if (window.dgProductTemplateInstalled) return;
  window.dgProductTemplateInstalled = true;
  const mobile = matchMedia('(max-width:749px)');
  const motion = () => matchMedia('(prefers-reduced-motion:reduce)').matches ? 'auto' : 'smooth';
  let frame = 0;
  function scheduleLayout() {
    if (!frame) frame = requestAnimationFrame(() => { frame = 0; layout(); });
  }
  function layout() {
    const root = document.querySelector('.dg-product-template');
    const enabled = !!root && mobile.matches;
    document.body.classList.toggle('dg-mobile-active', enabled);
    document.body.classList.toggle('dg-uplinkly-avoid', enabled && root.dataset.dgLanguage === 'true');
    document.body.classList.toggle('dg-uplinkly-compact', enabled && root.dataset.dgLanguageCompact === 'true');
    let height = 0, bottom = 14;
    if (enabled) {
      const dock = root.querySelector('dg-mobile-purchase');
      if (dock && !dock.hidden) height = dock.getBoundingClientRect().height;
      bottom += height;
      // Also avoid the in-page purchase controls when the sticky bar is hidden.
      if (!height) {
        const buttons = root.querySelector('.product-form__buttons');
        const rect = buttons?.getBoundingClientRect();
        if (rect && rect.bottom > 0 && rect.top < innerHeight) {
          bottom = Math.max(14, innerHeight - rect.top + 12);
          bottom = Math.min(bottom, innerHeight - 64);
        }
      }
    }
    document.documentElement.style.setProperty('--dg-purchase-height', `${height}px`);
    document.documentElement.style.setProperty('--dg-language-bottom', `${bottom}px`);
  }
  if (!customElements.get('dg-mobile-purchase')) customElements.define('dg-mobile-purchase', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      if (!this.root) return;
      this.button = this.querySelector('[data-dg-buy]');
      this.confirmed = !this.root.querySelector('variant-selects');
      this.onChange = e => {
        if (e.target.closest('variant-selects')) {
          this.confirmed = true;
          this.pending = true;
          this.button.disabled = true;
        }
        this.queueSync();
      };
      this.onClick = () => {
        const native = this.root.querySelector('product-form button[name="add"]');
        const options = this.root.querySelector('variant-selects');
        if (!this.confirmed && options) {
          options.scrollIntoView({behavior:motion(),block:'center'});
          options.querySelector('select,input:checked,input:not(:disabled)')?.focus({preventScroll:true});
          return; // Scrolling is not confirmation of a variant.
        }
        if (!this.pending && native && !native.disabled && native.getAttribute('aria-disabled') !== 'true') {
          native.click(); // Keeps quantity, selling plan, properties and app integration intact.
        }
      };
      this.onVariant = e => { if (String(e.data.sectionId) === this.root.dataset.section) { this.pending = false; this.queueSync(); } };
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariant);
      }
      this.button.addEventListener('click', this.onClick);
      this.root.addEventListener('change', this.onChange);
      this.observer = new MutationObserver(records => {
        if (records.some(r => !this.contains(r.target))) this.queueSync();
      });
      this.observer.observe(this.root,{subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['disabled','aria-disabled','class','value']});
      this.intersection = new IntersectionObserver(() => this.queueSync(),{threshold:[0,0.1,1]});
      this.resize = new ResizeObserver(scheduleLayout);
      this.resize.observe(this);
      this.onResize = () => this.queueSync();
      window.addEventListener('resize',this.onResize);
      this.sync();
    }
    queueSync() {
      if (!this.frame) this.frame = requestAnimationFrame(() => {this.frame=0;if(this.isConnected)this.sync();});
    }
    sync() {
      const native = this.root.querySelector('product-form button[name="add"]');
      if (!native) {this.hidden=true;scheduleLayout();return;}
      if (native !== this.native) {
        this.intersection.disconnect();this.intersection.observe(native);this.native=native;
      }
      // Dawn disables submit while requesting a new variant and re-enables it after rendering.
      // Unavailable combinations stay disabled; valid responses publish variantChange.
      const busy = native.disabled || native.getAttribute('aria-disabled') === 'true' || native.classList.contains('loading');
      if (this.pending && !busy) this.pending=false;
      const rect=native.getBoundingClientRect();
      const visible=rect.height>0 && rect.top<innerHeight && rect.bottom>0;
      this.hidden = !mobile.matches || visible;
      const label=!this.confirmed ? this.dataset.choose : (native.querySelector('span')?.textContent.trim() || native.textContent.trim());
      if(this.button.textContent!==label)this.button.textContent=label;
      this.button.disabled=busy || !!this.pending;
      const priceRoot=this.root.querySelector('[id^="price-"]');
      const price=priceRoot?.querySelector(priceRoot.querySelector('.price--on-sale') ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular');
      const value=price?.textContent.trim();
      if(value && this.querySelector('[data-dg-price]').textContent!==value)this.querySelector('[data-dg-price]').textContent=value;
      const values=[...this.root.querySelectorAll('variant-selects select')].map(e=>e.selectedOptions[0]?.textContent.trim()).concat([...this.root.querySelectorAll('variant-selects input:checked')].map(e=>e.value)).filter(Boolean).join(' / ');
      if(this.querySelector('[data-dg-options]').textContent!==values)this.querySelector('[data-dg-options]').textContent=values;
      scheduleLayout();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);this.observer?.disconnect();this.intersection?.disconnect();this.resize?.disconnect();this.unsubscribe?.();
      this.root?.removeEventListener('change',this.onChange);this.button?.removeEventListener('click',this.onClick);window.removeEventListener('resize',this.onResize);scheduleLayout();
    }
  });
  // A merchant description can already contain one outer Description accordion.
  // Remove only that redundant shell in the rendered DOM, retaining every content node.
  function normalizeDescription() {
    document.querySelectorAll('.dg-description-content').forEach(body => {
      const meaningful = [...body.childNodes].filter(n => n.nodeType === 1 || (n.nodeType === 3 && n.textContent.trim()));
      if (meaningful.length !== 1 || meaningful[0].nodeName !== 'DETAILS') return;
      const inner = meaningful[0];
      const summary = inner.querySelector(':scope > summary');
      if (!summary || !/^(product\s+)?description$/i.test(summary.textContent.trim())) return;
      summary.remove();
      inner.replaceWith(...inner.childNodes);
    });
  }
  document.addEventListener('click',event => {
    const link = event.target.closest('[data-dg-description-link]');
    if (!link) return;
    const target = document.getElementById(link.hash.slice(1));
    if (!target) return;
    event.preventDefault();
    target.open = true;
    requestAnimationFrame(() => {
      const heading = target.querySelector('summary');
      heading?.focus({preventScroll:true});
      target.scrollIntoView({behavior:motion(),block:'start'});
    });
  });
  if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded',normalizeDescription,{once:true}); else normalizeDescription();
  document.addEventListener('shopify:section:load',normalizeDescription);
  document.addEventListener('toggle',event=>{
    if(event.target.matches?.('.dg-description details') && !event.target.open) {
      event.target.querySelectorAll('video').forEach(video=>video.pause());
    }
  },true);
  window.addEventListener('scroll',scheduleLayout,{passive:true});
  window.addEventListener('resize',scheduleLayout);
  window.visualViewport?.addEventListener('resize',scheduleLayout);
  document.addEventListener('shopify:section:load',scheduleLayout);
  document.addEventListener('shopify:section:unload',scheduleLayout);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',scheduleLayout,{once:true});else scheduleLayout();
})();
